import { useState } from "react";

import {
  Alert,
  AlertDialog,
  Button,
  Colored,
  Modal,
  Text,
  TextArea,
} from "@vericus/cadmus-ui";

import { ApolloError } from "@apollo/client";
import { create, useModal } from "@ebay/nice-modal-react";
import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { GroupChangeRequestStatus, MessageFragment } from "@/generated/graphql";

import { useGroupChangeRequest } from "../../hooks/use-group-change-request";
import * as styles from "./group-change-request-modal.css";

const REASON_MAX_LENGTH = 500;

// Mirrors pantheon's `@group_change_request_ttl_seconds` — change both together.
const REQUEST_TTL_DAYS = 3;

// Pantheon reports every group-change rule violation with one of the repo's
// generic codes and puts the student-facing copy in `message`, so the server's
// wording is shown verbatim instead of being remapped here.
const SERVER_MESSAGE_CODES = ["BAD_REQUEST", "NOT_FOUND", "FORBIDDEN"];

function errorMessage(error: unknown): string {
  const fallback = "Something went wrong. Please try again.";
  if (!(error instanceof ApolloError)) return fallback;

  const [first] = error.graphQLErrors;
  if (first == null || !("code" in first) || typeof first.code !== "string") {
    return fallback;
  }
  if (!SERVER_MESSAGE_CODES.includes(first.code)) return fallback;

  const message = first.message.trim();
  return message === "" ? fallback : message;
}

export interface GroupChangeRequestModalProps {
  assessmentId: string;
  workGroupId: string;
  groupName: string;
  /** Pending request being edited. Omitted (or null) opens create mode. */
  existing?: MessageFragment | null;
}

export const GroupChangeRequestModal = create<GroupChangeRequestModalProps>(
  (props) => {
    const modal = useModal();
    return (
      <Modal.Root open={modal.visible}>
        <GroupChangeRequestModalContent
          {...props}
          onClose={() => modal.remove()}
        />
      </Modal.Root>
    );
  }
);

export interface GroupChangeRequestModalContentProps extends GroupChangeRequestModalProps {
  onClose: () => void;
}

// Exported so tests can render the form directly, without going through
// nice-modal's `show()`/`Provider` plumbing (see `finalise-group-modal.tsx`
// for the pattern this mirrors).
export function GroupChangeRequestModalContent(
  props: GroupChangeRequestModalContentProps
) {
  const { assessmentId, workGroupId, groupName, existing, onClose } = props;
  const isEdit =
    existing != null && existing.status === GroupChangeRequestStatus.Pending;

  const [reason, setReason] = useState(existing?.reason ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);

  const { send, update, withdraw } = useGroupChangeRequest(assessmentId);
  const { track } = useJitsu();

  const trimmed = reason.trim();
  const savedTrimmed = (existing?.reason ?? "").trim();

  const handleSend = async () => {
    if (trimmed === "") return;
    setSubmitting(true);
    setErrorText(null);
    try {
      await send(workGroupId, trimmed);
      track(JitsuEvent.GROUP_CHANGE_REQUEST_SENT, {
        reason_length: trimmed.length,
      });
      onClose();
    } catch (error) {
      setErrorText(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (existing == null || trimmed === "" || trimmed === savedTrimmed) return;
    setSubmitting(true);
    setErrorText(null);
    try {
      await update(workGroupId, existing.id, trimmed);
      track(JitsuEvent.GROUP_CHANGE_REQUEST_UPDATED, {});
      onClose();
    } catch (error) {
      setErrorText(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (existing == null) return;
    setConfirmingWithdraw(false);
    setSubmitting(true);
    setErrorText(null);
    try {
      await withdraw(workGroupId, existing.id);
      track(JitsuEvent.GROUP_CHANGE_REQUEST_WITHDRAWN, {});
      onClose();
    } catch (error) {
      setErrorText(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal.Content
      width={572}
      onCloseButtonClick={onClose}
      className={styles.content}
    >
      <div className={styles.header}>
        <Modal.Title asChild>
          <Text kind="headingFour">Request a group change</Text>
        </Modal.Title>
        <Modal.Description asChild>
          <Text kind="bodySm">
            Current group: <b>{groupName}</b>
          </Text>
        </Modal.Description>
      </div>

      <Text kind="bodyMd">
        Tell your teacher why you&rsquo;d like to move. You can have one request
        for this assessment; withdrawing it lets you send a new one.
      </Text>

      <div className={styles.reasonBlock}>
        <Text kind="label">
          Reason <Colored color="red500">*</Colored>
        </Text>
        <div className={styles.reasonField}>
          <TextArea.Input
            aria-label="Reason"
            className={styles.reasonInput}
            hideFocusRing
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            minRows={4}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <Text kind="systemMd" className={styles.counter}>
          {reason.length}/{REASON_MAX_LENGTH}
        </Text>
      </div>

      <Alert severity="info" size="sm">
        Your teacher will approve or decline and you&rsquo;ll see the outcome on
        your group card. Requests expire after {REQUEST_TTL_DAYS} days if not
        actioned, and an expired or declined request can&rsquo;t be resent.
      </Alert>

      {errorText != null && (
        <Alert severity="error" size="sm">
          {errorText}
        </Alert>
      )}

      <div className={styles.footer[isEdit ? "edit" : "create"]}>
        {isEdit && (
          <Button
            kind="error"
            onClick={() => setConfirmingWithdraw(true)}
            disabled={submitting}
          >
            Withdraw
          </Button>
        )}
        <div className={styles.footerRight}>
          <Button kind="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {isEdit ? (
            <Button
              kind="secondary"
              onClick={handleUpdate}
              disabled={
                submitting || trimmed === "" || trimmed === savedTrimmed
              }
            >
              Update
            </Button>
          ) : (
            <Button
              kind="secondary"
              onClick={handleSend}
              disabled={submitting || trimmed === ""}
            >
              Send request
            </Button>
          )}
        </div>
      </div>

      <AlertDialog.Root
        open={confirmingWithdraw}
        onOpenChange={setConfirmingWithdraw}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Withdraw your request?</AlertDialog.Title>
          <AlertDialog.Description>
            Your teacher won&apos;t see it and you&apos;ll stay in {groupName}.
            You can send a new request afterwards.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>Keep request</AlertDialog.Cancel>
            <AlertDialog.Action onClick={handleWithdraw}>
              Withdraw
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Modal.Content>
  );
}
